'use client';
import { useCallback, useState } from 'react';
import { Trans } from 'react-i18next';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/shared/button';
import { Heading } from '@/components/shared/heading';
import { Features } from '@/components/shared/features';
import { VerifiedBadge } from '@/components/shared/custom-icons';
import { useTranslation } from '@/contexts/language-context';
import { resizeImage } from '@/lib/resizeImage';
import { Card } from '@/components/shared/card';
import { checkLogin } from '../actions';
import {
  goToCodeSignIn,
  signInFeatureItems,
  TermsLink,
  PrivacyLink
} from '../shared';

/**
 * Merchant-led sign-in: shown when a provider has already started an
 * application for a known borrower (leadId + doctor + user object). Instead of
 * the account-entry form, the user is greeted by name and continues straight
 * into the one-time-code flow for their known account.
 */
export function MerchantLedForm({
  account,
  firstName,
  providerName,
  providerImage,
  error
}) {
  const router = useRouter();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const primaryAction = t('login.button.continue', 'Continue');

  const handleContinue = useCallback(async () => {
    setSubmitError(null);
    setIsLoading(true);
    try {
      const type = await checkLogin(account);
      if (type === 'credentials') {
        goToCodeSignIn(router, account);
      } else {
        // Any non-credentials result (new account redirect handled server-side)
        // just releases the button so the user isn't stuck on a spinner.
        setIsLoading(false);
      }
    } catch {
      setIsLoading(false);
      setSubmitError(
        t('login.error.generic', 'Something went wrong. Please try again.')
      );
    }
  }, [account, router, t]);

  return (
    <div className="flex flex-col gap-6">
      <div className="min-h-1" aria-hidden />
      <div className="flex flex-col gap-6">
        <Heading
          hero
          heading={`${t('login.return.merchantLedHeadingLine1', 'Complete Your')} ${t('login.return.merchantLedHeadingLine2', 'Beautifi Application')}`}
          className="text-center mb-4"
        />

        <Card as="section" selected>
          <div className="flex items-start gap-3">
            <div className="relative shrink-0 w-9 h-9 md:w-8 md:h-8">
              {providerImage ? (
                <img
                  src={resizeImage(providerImage, 'thumb')}
                  alt={providerName}
                  className="w-9 h-9 md:w-8 md:h-8 rounded-full object-cover bg-black/[0.02]"
                />
              ) : (
                <div className="w-9 h-9 md:w-8 md:h-8 rounded-full bg-neutral text-neutral-content flex items-center justify-center text-base font-light">
                  {providerName?.trim()?.charAt(0) || 'B'}
                </div>
              )}
              <VerifiedBadge className="absolute -bottom-0.5 -right-0.5 w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0 text-base-content">
              <p className="text-sm md:text-sm leading-snug">
                {t('login.returning.welcomeBackName', 'Welcome Back, {{name}}!', {
                  name: firstName
                })}
              </p>
              <p className="text-xs md:text-sm font-light tracking-tight leading-snug">
                {t(
                  'login.returning.merchantLedWelcome',
                  '{{doctor}} has started your application for your upcoming procedure. Complete it now to pick your monthly payment plan and checkout with Beautifi.',
                  { doctor: providerName }
                )}
              </p>
            </div>
          </div>
        </Card>

        <Card as="section">
          <Features items={signInFeatureItems(t)} />
        </Card>

        <Button
          onClick={handleContinue}
          disabled={isLoading}
          className="btn btn-neutral w-full rounded-full drop-shadow-[0_0_10px_rgba(84,107,219,0.3)]"
        >
          {isLoading && <span className="loading loading-spinner"></span>}
          {primaryAction}
        </Button>

        {(error || submitError) && (
          <div className="alert alert-error text-white rounded-field">
            <span>{error || submitError}</span>
          </div>
        )}

        <div className="text-xs text-center text-muted">
          <Trans
            t={t}
            i18nKey="login.agreement.merchantLed"
            values={{ primaryAction }}
          >
            By clicking “{{ primaryAction }}” you agree to Beautifi’s{' '}
            <TermsLink key="terms">
              Terms of Service
            </TermsLink>{' '}
            and{' '}
            <PrivacyLink key="privacy">
              Privacy Policy
            </PrivacyLink>
            , and consent to receive marketing communications from Beautifi
            and/or its affiliates.
          </Trans>
        </div>
      </div>
    </div>
  );
}
